import { KeyRoundIcon, SettingsIcon } from 'lucide-react'
import { Link } from 'react-router'

import { Wordmark } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { CodeBlock } from '@/components/CodeBlock'

const REQUIRED_ENV = `VITE_KEYCLOAK_ISSUER=https://<env>.saltseed.com/realms/saltseed
VITE_KEYCLOAK_CLIENT_ID=saltseed-spa`

function Centred({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="flex h-16 items-center border-b border-border px-6 lg:px-10">
        <Link to="/" className="text-foreground">
          <Wordmark />
        </Link>
      </header>
      <div className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-xl space-y-6">{children}</div>
      </div>
    </div>
  )
}

/** Shown when the build is missing its Keycloak variables. */
export function AuthConfigurationNotice() {
  return (
    <Centred>
      <div className="flex items-start gap-3">
        <div className="rounded-md border border-border bg-[var(--surface)] p-2 text-muted-foreground">
          <SettingsIcon className="size-4" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold tracking-[-0.02em] text-foreground">
            Sign-in is not configured
          </h1>
          <p className="text-sm text-pretty text-muted-foreground">
            This build of the saltseed console has no identity provider. Set the two
            Keycloak variables and rebuild — they are baked in at build time, so a running
            container cannot pick them up.
          </p>
        </div>
      </div>
      <CodeBlock code={REQUIRED_ENV} filename=".env" language="dotenv" />
      <p className="text-sm text-muted-foreground">
        The full list lives in{' '}
        <code className="font-mono text-foreground">frontend/.env.example</code>.
      </p>
    </Centred>
  )
}

/** Shown when a sign-in attempt fails and the user must retry deliberately. */
export function SignInPrompt({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}) {
  return (
    <Centred>
      <div className="flex items-start gap-3">
        <div className="rounded-md border border-border bg-[var(--surface)] p-2 text-muted-foreground">
          <KeyRoundIcon className="size-4" />
        </div>
        <div className="space-y-2">
          <h1 className="text-xl font-semibold tracking-[-0.02em] text-foreground">
            Sign-in did not complete
          </h1>
          <p className="text-sm text-pretty text-muted-foreground">{message}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={onRetry}>Try signing in again</Button>
        <Button variant="outline" asChild>
          <Link to="/">Back to the landing page</Link>
        </Button>
      </div>
    </Centred>
  )
}
